import React from 'react';
import ReactDOM from 'react-dom/client';
import { configureApi, configurePublicCatalog } from '@kss/api';
import { refreshSession, logout, resolveTenantFromHostname } from '@kss/auth';
import App from './App';
import { store } from './store';
import './index.css';

configureApi({
  getSession: () => store.getState().auth.session ?? null,
  setSession: (s) => store.dispatch(refreshSession(s)),
  onUnauthorized: () => {
    store.dispatch(logout('refresh-failed'));
  },
});

/**
 * Tenant resolver for the anonymous storefront catalog
 * (`/api/Public/*`). Priority:
 *
 *   1. The browser hostname — `kristy.eshope.com` → `kristy`,
 *      `sso.kristysoftware.com` → `sso`, etc. Hostname wins over
 *      localStorage so that a QR scan on a fresh mobile session
 *      (SSO(2) Issue 2) resolves to THIS deploy's tenant even if the
 *      browser happens to have a stale `kss.tenantCode` from an
 *      earlier visit to a different tenant.
 *   2. A persisted dev pin in `localStorage.kss.tenantCode` — only
 *      used when the hostname is bare (`localhost`, IP address, etc.).
 *   3. A compile-time default (`VITE_DEFAULT_TENANT_CODE`) — final
 *      safety net so `localhost:5173` isn't blank.
 *
 * Note: `session.tenantId` is intentionally NOT consulted here. The
 * public endpoints derive the tenant from `X-Tenant-Code`, and mixing
 * a hostname-scoped tenant with a JWT-scoped one caused subtle cross-
 * tenant reads when an admin had signed in to one tenant then
 * navigated to a different tenant's storefront in the same browser.
 */
const DEFAULT_TENANT_CODE =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    ?.VITE_DEFAULT_TENANT_CODE ?? 'sso';

configurePublicCatalog({
  getTenantCode: () => {
    const fromHost = resolveTenantFromHostname();
    if (fromHost) return fromHost;
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('kss.tenantCode');
        if (stored && stored.trim()) return stored.trim();
      }
    } catch {
      // private mode — fall through.
    }
    return DEFAULT_TENANT_CODE;
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
